import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { WhatsAppShareButton } from "@/components/shared/WhatsAppShareButton";
import { useEffect, useState } from "react";
import { useQueueStore } from "@/lib/store";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/lib/auth";
import { useServicesStore } from "@/lib/stores/services";
import { ServiceSelectionDialog } from "@/components/services/ServiceSelectionDialog";
import { Prescription, Service } from "@/types";
import { PatientPersonalInfo } from "@/components/patients/PatientPersonalInfo";
import { PatientConsultationDetails } from "@/components/patients/PatientConsultationDetails";
import { PatientServices } from "@/components/patients/PatientServices";
import { PatientPrescriptionHistory } from "@/components/patients/PatientPrescriptionHistory";
import { PatientQuickActions } from "@/components/patients/PatientQuickActions";
import { PatientPaymentSummary } from "@/components/patients/PatientPaymentSummary";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchPrescriptionsByPatient } from '@/lib/stores/prescriptions';
import { supabase } from '@/lib/supabase';
import { getPatientVitals, savePatientVitals, clearPatientVitals, VitalsData } from '@/lib/services/vitalsService';

const PatientView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthStore();
  const { services, fetchServices } = useServicesStore();
  const isDoctor = user?.role === 'doctor';
  
  const { 
    patients, 
    doctors, 
    updatePatientDoctor, 
    updatePatientStatus,
    updatePatientPayment,
    getPrescriptionsByPatient,
    addPatientServices,
    fetchPatients,
    fetchDoctors
  } = useQueueStore();
  
  const [patient, setPatient] = useState(patients.find((p) => p.id === id));
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  
  // Vitals state
  const [vitals, setVitals] = useState<VitalsData | null>(null);
  const [isLoadingVitals, setIsLoadingVitals] = useState(false);

  // Load vitals from database when patient changes
  useEffect(() => {
    const loadVitals = async () => {
      if (id) {
        setIsLoadingVitals(true);
        try {
          const vitalsData = await getPatientVitals(id);
          setVitals(vitalsData);
        } catch (error) {
          console.error('Error loading vitals:', error);
        } finally {
          setIsLoadingVitals(false);
        }
      }
    };
    
    loadVitals();
  }, [id]);

  // Clear vitals function
  const clearVitals = async () => {
    if (id) {
      try {
        await clearPatientVitals(id);
        setVitals(null);
        toast({
          title: "Vitals Cleared",
          description: "Patient vitals have been cleared",
        });
      } catch (error) {
        console.error('Error clearing vitals:', error);
        toast({
          title: "Error",
          description: "Failed to clear vitals",
          variant: "destructive",
        });
      }
    }
  };
  
  useEffect(() => {
    // Fetch services and doctors when component mounts
    fetchServices();
    fetchDoctors();
  }, [fetchServices, fetchDoctors]);
  
  useEffect(() => {
    setPatient(patients.find((p) => p.id === id));
    // Fetch prescriptions from Supabase
    if (id) {
      fetchPrescriptionsByPatient(id).then((prescriptions) => {
        setPrescriptions(prescriptions);
      });
    }
  }, [id, patients]);
  
  const handleChangeDoctor = (doctorId: string) => {
    if (id) {
      updatePatientDoctor(id, doctorId);
      toast({
        title: "Doctor assigned",
        description: `The patient has been assigned to ${doctors.find(d => d.id === doctorId)?.name}`,
      });
      
      // Update local state
      setPatient(patients.find((p) => p.id === id));
    }
  };
  
  const handleStatusChange = (status: string) => {
    if (id) {
      updatePatientStatus(id, status as any);
      toast({
        title: "Status updated",
        description: `The patient status has been updated to ${status}`,
      });
      
      // Update local state
      setPatient(patients.find((p) => p.id === id));
    }
  };

  const handleAddService = () => {
    setIsServiceDialogOpen(true);
  };
  
  const handleServiceConfirm = (selectedServices: Service[]) => {
    if (id && selectedServices.length > 0) {
      addPatientServices(id, selectedServices);
      
      // Update local state
      setPatient(patients.find((p) => p.id === id));
      
      // Show success message
      const total = selectedServices.reduce((sum, service) => sum + service.price, 0);
      toast({
        title: "Services added",
        description: `${selectedServices.length} service(s) added successfully. Total: ₹${total.toFixed(2)}`,
      });
    }
  };

  const handlePaymentUpdate = async (amount: number, paymentMode: string) => {
    if (id) {
      // Defensive: always use lowercase and only allowed values
      let mode = (paymentMode || '').toLowerCase();
      if (mode === 'netbanking' || mode === 'online') mode = 'upi';
      const allowedModes = ['cash', 'card', 'upi', 'insurance'];
      if (!allowedModes.includes(mode)) mode = 'cash';
      await updatePatientPayment(id, amount, mode as any);
      // Re-fetch patients to get the latest data
      await fetchPatients();
      setPatient(patients.find((p) => p.id === id));
    }
  };
  
  if (!patient) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <h2 className="text-xl font-semibold mb-2">Patient not found</h2>
          <p className="text-gray-500 mb-4">The patient you are looking for does not exist.</p>
          <Button asChild>
            <Link to="/queue">Back to Queue</Link>
          </Button>
        </div>
      </MainLayout>
    );
  }

  // Format the badge variant based on status
  const getBadgeVariant = (status: string) => {
    switch (status) {
      case 'waiting':
        return 'warning';
      case 'in-consultation':
        return 'default';
      case 'completed':
        return 'success';
      case 'no-show':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Fix: Changed from 'reception' to 'staff' to match the defined role types
  const isReceptionStaff = user?.role === 'staff';

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => {
                if (user?.role === 'doctor') {
                  navigate('/doctor-dashboard');
                } else if (user?.role === 'staff') {
                  navigate('/queue');
                } else {
                  navigate('/');
                }
              }}
              className="w-full sm:w-auto"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold">Patient Details</h1>
          </div>
          <div className="flex space-x-2">
            <WhatsAppShareButton
              className="w-full sm:w-auto"
              phoneNumber={patient.contactNumber}
              patientName={patient.name}
              patientId={patient.id}
              reason={patient.reason}
              doctorName={doctors.find((d) => d.id === patient.doctorId)?.name}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information Card */}
            <PatientPersonalInfo 
              patient={patient} 
              doctors={doctors} 
              handleChangeDoctor={handleChangeDoctor}
              isDoctor={isDoctor}
              statusBadgeVariant={getBadgeVariant(patient.status)}
            />

            {/* Services Card */}
            <PatientServices 
              patient={patient} 
              onServiceRemoved={() => {
                // Refresh patient data after service removal
                setPatient(patients.find((p) => p.id === id));
              }}
            />
            
            {/* Prescription History Card with print button for reception */}
            <PatientPrescriptionHistory 
              prescriptions={prescriptions} 
              patientId={id || ''} 
            />
          </div>

          <div className="space-y-6">
            {/* Quick Actions Card */}
            <PatientQuickActions 
              patientId={id || ''}
              handleAddService={handleAddService}
              handlePaymentUpdate={handlePaymentUpdate}
              handleStatusChange={handleStatusChange}
              isDoctor={isDoctor}
              hasPrescriptions={prescriptions.length > 0}
              patient={patient}
            />
            
            {/* Payment Summary Card - only show for non-doctors */}
            {!isDoctor && (
              <PatientPaymentSummary patient={patient} />
            )}
            
            {/* Vitals Block */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {vitals ? 'Patient Vitals (Current Visit)' : 'Enter Patient Vitals'}
                </CardTitle>
                <CardDescription>
                  {vitals ? 'Vitals recorded for this visit' : 'Record patient vitals for this visit - Empty fields will appear with dashed borders'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isLoadingVitals ? (
                  <div className="text-center py-4">Loading vitals...</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="bp-systolic">BP (Systolic)</Label>
                        <Input
                          id="bp-systolic"
                          type="number"
                          value={vitals?.bpSystolic || ''}
                          onChange={async (e) => {
                            const newVitals = { ...vitals, bpSystolic: e.target.value, recordedBy: user?.id || 'unknown' };
                            setVitals(newVitals);
                            try {
                              await savePatientVitals(id!, newVitals);
                            } catch (error) {
                              console.error('Error saving vitals:', error);
                            }
                          }}
                          placeholder="Enter systolic BP"
                          className={`text-sm ${!vitals?.bpSystolic ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bp-diastolic">BP (Diastolic)</Label>
                        <Input
                          id="bp-diastolic"
                          type="number"
                          value={vitals?.bpDiastolic || ''}
                          onChange={async (e) => {
                            const newVitals = { ...vitals, bpDiastolic: e.target.value, recordedBy: user?.id || 'unknown' };
                            setVitals(newVitals);
                            try {
                              await savePatientVitals(id!, newVitals);
                            } catch (error) {
                              console.error('Error saving vitals:', error);
                            }
                          }}
                          placeholder="Enter diastolic BP"
                          className={`text-sm ${!vitals?.bpDiastolic ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                        />
                      </div>
                    </div>
                
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="temperature">Temperature (°F)</Label>
                        <Input
                          id="temperature"
                          type="number"
                          step="0.1"
                          value={vitals?.temperature || ''}
                          onChange={async (e) => {
                            const newVitals = { ...vitals, temperature: e.target.value, recordedBy: user?.id || 'unknown' };
                            setVitals(newVitals);
                            try {
                              await savePatientVitals(id!, newVitals);
                            } catch (error) {
                              console.error('Error saving vitals:', error);
                            }
                          }}
                          placeholder="Enter temperature (°F)"
                          className={`text-sm ${!vitals?.temperature ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pulse">Pulse (BPM)</Label>
                        <Input
                          id="pulse"
                          type="number"
                          value={vitals?.pulse || ''}
                          onChange={async (e) => {
                            const newVitals = { ...vitals, pulse: e.target.value, recordedBy: user?.id || 'unknown' };
                            setVitals(newVitals);
                            try {
                              await savePatientVitals(id!, newVitals);
                            } catch (error) {
                              console.error('Error saving vitals:', error);
                            }
                          }}
                          placeholder="Enter pulse (BPM)"
                          className={`text-sm ${!vitals?.pulse ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                        />
                      </div>
                    </div>
                
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="respiratory-rate">Respiratory Rate</Label>
                        <Input
                          id="respiratory-rate"
                          type="number"
                          value={vitals?.respiratoryRate || ''}
                          onChange={async (e) => {
                            const newVitals = { ...vitals, respiratoryRate: e.target.value, recordedBy: user?.id || 'unknown' };
                            setVitals(newVitals);
                            try {
                              await savePatientVitals(id!, newVitals);
                            } catch (error) {
                              console.error('Error saving vitals:', error);
                            }
                          }}
                          placeholder="Enter respiratory rate"
                          className={`text-sm ${!vitals?.respiratoryRate ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="oxygen-saturation">SpO2 (%)</Label>
                        <Input
                          id="oxygen-saturation"
                          type="number"
                          value={vitals?.oxygenSaturation || ''}
                          onChange={async (e) => {
                            const newVitals = { ...vitals, oxygenSaturation: e.target.value, recordedBy: user?.id || 'unknown' };
                            setVitals(newVitals);
                            try {
                              await savePatientVitals(id!, newVitals);
                            } catch (error) {
                              console.error('Error saving vitals:', error);
                            }
                          }}
                          placeholder="Enter SpO2 (%)"
                          className={`text-sm ${!vitals?.oxygenSaturation ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                        />
                      </div>
                    </div>
                
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="weight">Weight (kg)</Label>
                        <Input
                          id="weight"
                          type="number"
                          step="0.1"
                          value={vitals?.weight || ''}
                          onChange={async (e) => {
                            const weight = e.target.value;
                            const height = vitals?.height || '';
                            let bmi = vitals?.bmi || '';
                            
                            // Auto-calculate BMI
                            if (weight && height) {
                              bmi = (parseFloat(weight) / Math.pow(parseFloat(height) / 100, 2)).toFixed(1);
                            }
                            
                            const newVitals = { ...vitals, weight, bmi, recordedBy: user?.id || 'unknown' };
                            setVitals(newVitals);
                            try {
                              await savePatientVitals(id!, newVitals);
                            } catch (error) {
                              console.error('Error saving vitals:', error);
                            }
                          }}
                          placeholder="Enter weight (kg)"
                          className={`text-sm ${!vitals?.weight ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="height">Height (cm)</Label>
                        <Input
                          id="height"
                          type="number"
                          value={vitals?.height || ''}
                          onChange={async (e) => {
                            const height = e.target.value;
                            const weight = vitals?.weight || '';
                            let bmi = vitals?.bmi || '';
                            
                            // Auto-calculate BMI
                            if (height && weight) {
                              bmi = (parseFloat(weight) / Math.pow(parseFloat(height) / 100, 2)).toFixed(1);
                            }
                            
                            const newVitals = { ...vitals, height, bmi, recordedBy: user?.id || 'unknown' };
                            setVitals(newVitals);
                            try {
                              await savePatientVitals(id!, newVitals);
                            } catch (error) {
                              console.error('Error saving vitals:', error);
                            }
                          }}
                          placeholder="Enter height (cm)"
                          className={`text-sm ${!vitals?.height ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="bmi">BMI</Label>
                        <Input
                          id="bmi"
                          type="number"
                          step="0.1"
                          value={vitals?.bmi || ''}
                          placeholder="Auto-calculated"
                          className={`text-sm ${!vitals?.bmi ? 'border-dashed border-gray-300 bg-gray-50' : 'border-solid border-gray-300 bg-white'}`}
                          readOnly
                        />
                      </div>
                    </div>
                
                    <div className="flex justify-between pt-2">
                      <Button 
                        onClick={clearVitals}
                        variant="outline"
                        size="sm"
                      >
                        Clear Vitals
                      </Button>
                      <div className="text-sm text-muted-foreground">
                        {vitals?.recordedAt && (
                          <span>Last updated: {new Date(vitals.recordedAt).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <ServiceSelectionDialog 
        isOpen={isServiceDialogOpen}
        setIsOpen={setIsServiceDialogOpen}
        services={services}
        onConfirm={handleServiceConfirm}
      />
    </MainLayout>
  );
};

export default PatientView;
